/**
 * Label colour against an arbitrary event-type colour.
 *
 * Entry colours are user-chosen, so the text drawn on them has to be computed
 * rather than picked. 03-ui-spec.md section 1.2 requires 4.5:1 minimum, which
 * is not something you can eyeball across an arbitrary palette.
 */

/** Parses #rgb, #rrggbb or #rrggbbaa. Returns null when unparseable. */
export function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const s = hex.trim().replace(/^#/, '')

  if (s.length === 3) {
    const r = s[0]
    const g = s[1]
    const b = s[2]
    if (!r || !g || !b) return null
    const v = (c: string) => parseInt(c + c, 16)
    const out = { r: v(r), g: v(g), b: v(b) }
    return Number.isNaN(out.r + out.g + out.b) ? null : out
  }

  if (s.length === 6 || s.length === 8) {
    const out = {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
    }
    return Number.isNaN(out.r + out.g + out.b) ? null : out
  }

  return null
}

/** Undoes sRGB gamma encoding for one channel. */
function linearise(channel8Bit: number): number {
  const c = channel8Bit / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/**
 * WCAG relative luminance, 0 (black) to 1 (white).
 *
 * Genuinely gamma-corrected, not HSL lightness. The two diverge most for
 * saturated colours -- which is exactly what event types are.
 */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0

  return (
    0.2126 * linearise(rgb.r) + 0.7152 * linearise(rgb.g) + 0.0722 * linearise(rgb.b)
  )
}

/**
 * 0.179 is the exact crossover where black and white text give equal contrast
 * ratio against a background.
 *
 * Do not round this to something tidier. At 0.55 -- a plausible-looking guess --
 * mid-bright colours get white text at roughly 2.4:1, which fails the 4.5:1
 * requirement while looking fine to whoever chose it.
 */
export const LUMINANCE_CROSSOVER = 0.179

export function labelColorOn(backgroundHex: string): '#000000' | '#ffffff' {
  return relativeLuminance(backgroundHex) > LUMINANCE_CROSSOVER ? '#000000' : '#ffffff'
}

/** WCAG contrast ratio between two colours, 1:1 to 21:1. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

/** WCAG AA for normal-size text. */
export function meetsAA(foreground: string, background: string): boolean {
  return contrastRatio(foreground, background) >= 4.5
}
